import { provide, ref } from 'vue';
import i18n from '@/i18n.js';
import { titleCaseText } from '@/utils/string-formatting.js';

export function useApplicationCore(props) {
    const app = ref({});
    const state = ref({});
    const processedApplication = ref({});

    const allowedLanguages = ['en', 'fr', 'es'];
    const allowedCountries = ['CA', 'US'];
    const locale = window.App?.locale;

    const config = ref({
        lang: allowedLanguages.includes(locale) ? locale : 'en',
        isLoading: false,
        country: allowedCountries.includes(props.country?.toUpperCase()) ? props.country.toUpperCase() : 'CA'
    });

    function updateApplication(obj) {
        app.value = { ...app.value, ...obj };
    }

    function updateApplicationField(key, value) {
        app.value[key] = value;
    }

    function persistLocally() {
        // [DISABLED] Writes app data to localStorage/sessionStorage under 'comparehub_loanscanada'.
        // The read side (getLocalData in useApplicationLifecycle.js) never uses the stored data.
        // To re-enable: uncomment the line below AND the getLocalStorage() call in
        // useApplicationLifecycle.js > getLocalData(), then consume the data there.
        // Import: import { setLocalStorage } from '@/storage-helper.js';
        //
        // setLocalStorage(app.value);
    }

    updateApplicationField('forcePhoneVerification', true);

    updateApplicationField('lc_contact', '');
    updateApplicationField('lc_referral', '');

    if (props.formNonce) {
        updateApplicationField('form_nonce', props.formNonce);
    }

    function getTranslatedText(originalText = null, key = null) {
        if (!originalText) {
            return null;
        }
        // these translation_keys are being shared globally from DomainServiceProvider & added
        // to the window object on the layout blade file
        let keysToChange = window.App?.translation_keys ?? { brand: 'Loans Canada' };
        // need to wrap the text to be translated in double underscores like so: __requested_amount__
        // we can specify a key to be used in the translation file like so: this.getTranslatedText('__requested_amount__', 'steps.titles')
        let translated = originalText.replace(/__(.*?)__/, (match, stringToTranslate) => {
            const t = i18n.global.t;
            return !key ? t(stringToTranslate, keysToChange) : t(key + "." + stringToTranslate, keysToChange);
        });

        // capitalize only EN
        const keysToBeTitleCapitalized = ['fields.labels', 'steps.titles'];
        if (config?.value.lang !== 'fr' && config?.value.lang !== 'es') {
            if (translated && translated.length && key && keysToBeTitleCapitalized.includes(key)) {
                // visible text only: some titles and labels carry markup (classes, an SVG icon)
                translated = titleCaseText(translated);
            }
        }
        return translated;
    }

    function provideApp() {
        provide('app', {
            app,
            state,
            config,
            updateApplication,
            updateApplicationField,
            persistLocally,
            processedApplication,
            getTranslatedText,
        });
    }

    return {
        app,
        state,
        config,
        processedApplication,
        updateApplication,
        updateApplicationField,
        persistLocally,
        getTranslatedText,
        provideApp,
    };
}
